import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const GRID = [
  { src: IMAGES.heroShowroom, label: "Premium Showroom · Mumbai", h: "aspect-[4/5]" },
  { src: IMAGES.contactus, label: "Factory Floor · Morbi", h: "aspect-[3/4]" },
  { src: IMAGES.manufacturingImage, label: "Slider Display Stand", h: "aspect-square" },
  { src: IMAGES.manufacturing, label: "Booklet Display Stand", h: "aspect-[3/4]" },
  { src: IMAGES.AboutsUS, label: "Showroom Detail", h: "aspect-[4/3]" },
  { src: IMAGES.Glides, label: "Wall Display System", h: "aspect-[3/4]" },
  { src: IMAGES.GlideHero, label: "Welding Bay", h: "aspect-square" },
  { src: IMAGES.GlideVertical, label: "Large Format Tile Display", h: "aspect-[4/5]" },
  { src: IMAGES.GlideDuo, label: "Luxury Retail Fitout · Dubai", h: "aspect-[3/4]" },
  { src: IMAGES.Gallery, label: "Tile Sample Wall", h: "aspect-square" },
  { src: IMAGES.logo, label: "Architect Specification", h: "aspect-[4/3]" },
];

export default function Gallery() {
  const [activeIdx, setActiveIdx] = useState(null);
  const isOpen = activeIdx !== null;

  useEffect(() => {
    const onKey = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") setActiveIdx(null);
      if (e.key === "ArrowRight") setActiveIdx((i) => (i + 1) % GRID.length);
      if (e.key === "ArrowLeft") setActiveIdx((i) => (i - 1 + GRID.length) % GRID.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  return (
    <div data-testid="gallery-page">
      <PageHero
        overline="Gallery"
        title="Factory, showrooms, installations."
        subtitle="A visual tour of Stenzia's engineering, finishing and real-world installations across India and abroad."
        image={IMAGES.Gallery}
      />

      {<section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="mb-12">
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Visual Archive</div>
            <h2 className="mt-4 font-display font-light text-3xl md:text-5xl tracking-tighter text-charcoal leading-[1.05] max-w-2xl">
              A closer look at every detail.
            </h2>
          </Reveal>

          {/* Pinterest-style masonry using CSS columns */}
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {GRID.map((g, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                className="break-inside-avoid"
              >
                <button
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setActiveIdx(i)}
                  className={`relative w-full overflow-hidden rounded-2xl group cursor-pointer ${g.h}`}
                  aria-label={g.label}
                >
                  <img src={g.src} alt={g.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-left translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="overline text-stenzia-gold text-[10px]">View</div>
                    <div className="mt-1 text-white font-display text-base tracking-tight">{g.label}</div>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActiveIdx(null)}
            className="fixed inset-0 z-[60] bg-charcoal/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white text-white hover:text-charcoal flex items-center justify-center transition-all"
              onClick={(e) => { e.stopPropagation(); setActiveIdx(null); }}
              aria-label="Close lightbox"
            >
              <X size={22} />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setActiveIdx((i) => (i - 1 + GRID.length) % GRID.length); }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-stenzia-gold text-white hover:text-charcoal flex items-center justify-center transition-all"
              aria-label="Previous"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); setActiveIdx((i) => (i + 1) % GRID.length); }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-stenzia-gold text-white hover:text-charcoal flex items-center justify-center transition-all"
              aria-label="Next"
            >
              <ChevronRight size={22} />
            </button>

            <motion.div
              key={activeIdx}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={GRID[activeIdx].src} alt={GRID[activeIdx].label} className="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl" />
              <div className="mt-5 flex items-center justify-between text-white/80 text-sm">
                <div className="overline text-stenzia-gold">{String(activeIdx + 1).padStart(2, "0")} / {String(GRID.length).padStart(2, "0")}</div>
                <div className="font-display text-base">{GRID[activeIdx].label}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
